import React, { useEffect } from "react";
import styled from "styled-components";
import { typographyCSS } from "@ifgengineering/design-tokens/legacy";
import { devReportIgnoreProps } from "./capture";
import { DEV_REPORT, NOTE_MAX_LENGTH } from "./content";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${({ theme }) => theme.zIndex.topmost};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.space.screen.inset}px;
  background: ${({ theme }) => theme.colors.ui.background.glass};
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.card.gap}px;
  width: 100%;
  max-width: 480px;
  max-height: 100%;
  overflow-y: auto;
  padding: ${({ theme }) => theme.space.card.inset}px;
  border-radius: ${({ theme }) => theme.radius.lg}px;
  background: ${({ theme }) => theme.colors.ui.background.raised};
  box-shadow: ${({ theme }) => theme.elevation.high};
`;

const Title = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.ui.text.primary};
  ${typographyCSS("heading.2xs")}
`;

const Subtitle = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.ui.text.secondary};
  ${typographyCSS("body.sm")}
`;

const Preview = styled.img`
  width: 100%;
  max-height: 220px;
  object-fit: contain;
  border: 1px solid ${({ theme }) => theme.colors.ui.border.subtle};
  border-radius: ${({ theme }) => theme.radius.sm}px;
  background: ${({ theme }) => theme.colors.ui.background.inset};
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm}px;
  color: ${({ theme }) => theme.colors.ui.text.primary};
  ${typographyCSS("caption.sm-medium")}
`;

const Note = styled.textarea`
  min-height: 96px;
  padding: ${({ theme }) => theme.space.md}px;
  border: 1px solid ${({ theme }) => theme.colors.ui.border.default};
  border-radius: ${({ theme }) => theme.radius.sm}px;
  background: ${({ theme }) => theme.colors.ui.background.raised};
  color: ${({ theme }) => theme.colors.ui.text.primary};
  resize: vertical;
  ${typographyCSS("body.md")}

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.ui.border.focus};
  }
`;

const Message = styled.p<{ tone: "error" | "success" }>`
  margin: 0;
  padding: ${({ theme }) => theme.space.md}px;
  border-radius: ${({ theme }) => theme.radius.sm}px;
  background: ${({ theme, tone }) =>
    tone === "error"
      ? theme.colors.ui.background.error
      : theme.colors.ui.background.success};
  color: ${({ theme }) => theme.colors.ui.text.primary};
  ${typographyCSS("body.sm")}
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.space.sm}px;
`;

const Action = styled.button<{ emphasis: "primary" | "secondary" }>`
  height: ${({ theme }) => theme.size.control.md}px;
  padding: 0 ${({ theme }) => theme.space.card.inset}px;
  border: 1px solid
    ${({ theme, emphasis }) =>
      emphasis === "primary"
        ? theme.colors.ui.border.brand
        : theme.colors.ui.border.default};
  border-radius: ${({ theme }) => theme.radius.full}px;
  background: ${({ theme, emphasis }) =>
    emphasis === "primary"
      ? theme.colors.ui.border.brand
      : theme.colors.ui.background.raised};
  color: ${({ theme, emphasis }) =>
    emphasis === "primary"
      ? theme.colors.ui.text.onFill
      : theme.colors.ui.text.primary};
  cursor: pointer;
  ${typographyCSS("label.sm")}
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface DevReportModalProps {
  screenshotUrl: string | null;
  note: string;
  error: string | null;
  isSending: boolean;
  isSent: boolean;
  onNoteChange: (note: string) => void;
  onSubmit: () => void;
  onClose: () => void;
}

export const DevReportModal = ({
  screenshotUrl,
  note,
  error,
  isSending,
  isSent,
  onNoteChange,
  onSubmit,
  onClose,
}: DevReportModalProps): JSX.Element => {
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isSending) onClose();
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [onClose, isSending]);

  return (
    <Overlay
      {...devReportIgnoreProps}
      onClick={isSending ? undefined : onClose}
      data-testid="dev-report-overlay"
    >
      <Panel
        role="dialog"
        aria-modal="true"
        aria-label={DEV_REPORT.title}
        onClick={(event) => event.stopPropagation()}
      >
        <Title>{isSent ? DEV_REPORT.sentTitle : DEV_REPORT.title}</Title>
        <Subtitle>
          {isSent ? DEV_REPORT.sentBody : DEV_REPORT.subtitle}
        </Subtitle>

        {isSent ? (
          <Actions>
            <Action
              type="button"
              emphasis="primary"
              onClick={onClose}
              data-testid="dev-report-close"
            >
              {DEV_REPORT.close}
            </Action>
          </Actions>
        ) : (
          <>
            {screenshotUrl ? (
              <Preview
                src={screenshotUrl}
                alt={DEV_REPORT.screenshotAlt}
                data-testid="dev-report-thumbnail"
              />
            ) : null}

            <Field>
              {DEV_REPORT.noteLabel}
              <Note
                autoFocus
                value={note}
                maxLength={NOTE_MAX_LENGTH}
                placeholder={DEV_REPORT.notePlaceholder}
                onChange={(event) => onNoteChange(event.target.value)}
                data-testid="dev-report-note-input"
              />
            </Field>

            {error ? (
              <Message tone="error" role="alert" data-testid="dev-report-error">
                {error}
              </Message>
            ) : null}

            <Actions>
              <Action
                type="button"
                emphasis="secondary"
                onClick={onClose}
                disabled={isSending}
                data-testid="dev-report-cancel"
              >
                {DEV_REPORT.cancel}
              </Action>
              <Action
                type="button"
                emphasis="primary"
                disabled={!screenshotUrl || isSending}
                onClick={onSubmit}
                data-testid="dev-report-submit"
              >
                {isSending ? DEV_REPORT.submitting : DEV_REPORT.submit}
              </Action>
            </Actions>
          </>
        )}
      </Panel>
    </Overlay>
  );
};
